import './admin-navbar.scss';

import { Menu } from 'antd';
import { type ItemType, type MenuItemType } from 'antd/es/menu/interface';
import { match } from 'path-to-regexp';
import { type FC, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';

import {
  ADMIN_ROUTES,
  ROUTES,
  getFullAdminRoute
} from '@/components/Shared/routes/routes';
import { NavbarBadge } from '@/components/Shared/ui';
import { WIDTH_FOR_OPENED_NAVBAR } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import {
  BankOutlined,
  CarOutlined,
  CopyOutlined,
  FundProjectionScreenOutlined,
  IdcardOutlined,
  LogoutOutlined,
  ProfileOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  ShopOutlined,
  UserOutlined
} from '@/media/antd-icons';
import IconMessage from '@/media/icons/message.svg?react';
import ServiceDeskIcon from '@/media/icons/service-desk.svg?react';
import IconModules from '@/media/svg/Filter.svg?react';
import IconPlus from '@/media/svg/element-plus.svg?react';
import IconLeasing from '@/media/svg/percent.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSidebarExpanded } from '@/store/userInfo.slice';

type TAdminNavBarProps = {
  isSidebarExpanded: boolean;
};

export const adminSideBarItems = [
  {
    label: 'На главную',
    key: ROUTES.autoCredit,
    icon: <LogoutOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Дилерские центры',
    key: getFullAdminRoute(ADMIN_ROUTES.dealers),
    icon: <ShopOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Банки',
    key: getFullAdminRoute(ADMIN_ROUTES.banks),
    icon: <BankOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Клиенты',
    key: getFullAdminRoute(ADMIN_ROUTES.clients),
    icon: <IdcardOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Лизинговые компании',
    key: getFullAdminRoute(ADMIN_ROUTES.leasings),
    icon: <IconLeasing className="admin-navbar__link-icon" />
  },
  {
    label: 'Продукты',
    key: getFullAdminRoute(ADMIN_ROUTES.products),
    icon: <ProjectOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Авто',
    key: getFullAdminRoute(ADMIN_ROUTES.auto),
    icon: <CarOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Пользователи',
    key: getFullAdminRoute(ADMIN_ROUTES.users),
    icon: <UserOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Модули',
    key: getFullAdminRoute(ADMIN_ROUTES.modules),
    icon: <IconModules className="admin-navbar__link-icon" />
  },
  {
    label: 'Справочники',
    key: getFullAdminRoute(ADMIN_ROUTES.directory),
    icon: <ReadOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Файлы',
    key: getFullAdminRoute(ADMIN_ROUTES.files),
    icon: <CopyOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Внешние сервисы',
    key: getFullAdminRoute(ADMIN_ROUTES.externalServices),
    icon: <IconPlus className="admin-navbar__link-icon" />
  },
  {
    label: 'Логи',
    key: getFullAdminRoute(ADMIN_ROUTES.logs),
    icon: <ProfileOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Отчеты',
    key: getFullAdminRoute(ADMIN_ROUTES.reports),
    icon: <FundProjectionScreenOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Поддержка',
    key: getFullAdminRoute(ADMIN_ROUTES.faq),
    icon: <QuestionCircleOutlined className="admin-navbar__link-icon" />
  },
  {
    label: 'Системные уведомления',
    key: getFullAdminRoute(ADMIN_ROUTES.systemNotifications),
    icon: <IconMessage className="admin-navbar__link-icon" />
  }
];

export const AdminNavBar: FC<TAdminNavBarProps> = ({ isSidebarExpanded }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [windowWidth] = useWindowSize();

  const { newRequestsAdminBadge, reworkRequestsAdminBadge } = useAppSelector(
    (state) => state.serviceDesk
  );

  const onClick = () => {
    if (windowWidth < WIDTH_FOR_OPENED_NAVBAR)
      dispatch(setSidebarExpanded(false));
  };

  const menuItems: ItemType<MenuItemType>[] = useMemo(() => {
    const items: ItemType<MenuItemType>[] = adminSideBarItems.map((item) => {
      let isActive = location.pathname.includes(item.key);

      if (item.key === getFullAdminRoute(ADMIN_ROUTES.leasings)) {
        const leasingCompanyMatch = match(
          [
            getFullAdminRoute(ADMIN_ROUTES.individualLeasingCompany()),
            getFullAdminRoute(ADMIN_ROUTES.legalEntityLeasingCompany())
          ],
          { decode: decodeURIComponent }
        );
        isActive ||= !!leasingCompanyMatch(location.pathname);
      }

      return {
        key: item.key,
        icon: item.icon,
        label: (
          <NavLink
            to={item.key}
            onClick={onClick}
            className="admin-navbar__link"
          >
            {item.label}
          </NavLink>
        ),
        className: `admin-navbar__menu-item ${isActive ? 'admin-navbar__link--active' : ''}`
      };
    });

    const serviceDeskIsActive = location.pathname.includes(ROUTES.serviceDesk);

    items.push({
      key: ROUTES.serviceDesk,
      icon: <ServiceDeskIcon className="admin-navbar__link-icon" />,
      label: (
        <NavLink
          to={ROUTES.serviceDesk}
          onClick={onClick}
          className="admin-navbar__link"
        >
          Service Desk
          <NavbarBadge
            isSidebarExpanded={isSidebarExpanded}
            count={newRequestsAdminBadge + reworkRequestsAdminBadge}
          />
        </NavLink>
      ),
      className: `admin-navbar__menu-item ${serviceDeskIsActive ? 'admin-navbar__link--active' : ''}`
    });

    return items;
  }, [
    location.pathname,
    isSidebarExpanded,
    newRequestsAdminBadge,
    reworkRequestsAdminBadge,
    onClick
  ]);

  return (
    <Menu
      mode="inline"
      theme="dark"
      inlineCollapsed={!isSidebarExpanded}
      className="admin-navbar"
      items={menuItems}
      style={{
        marginLeft: isSidebarExpanded ? '-16px' : '0px',
        width: !isSidebarExpanded ? '50px' : '230px'
      }}
    />
  );
};
